/**
 * <snd-hscroll> — one line, dragged sideways, with the edges fading out (SND-16955).
 *
 * Wraps a row that must never wrap: the breadcrumb trail and the PDP type +
 * key-point row. The element itself is the scroll container; its single child
 * is the row. Two custom properties drive the mask in `snippets/snd-hscroll.liquid`:
 *
 *   --snd-hs-l / --snd-hs-r  →  0px, or --snd-hs-fade when there is more to see
 *                               on that side. So nothing is faded at rest, and
 *                               the fade only ever hides content that IS there.
 *
 * Touch scrolls natively. A mouse gets the same drag, with a 4px threshold so a
 * click on a breadcrumb link stays a click; the click that ends a real drag is
 * swallowed once.
 */
(function () {
  if (typeof customElements === 'undefined' || customElements.get('snd-hscroll')) return;

  const EDGE = 1;
  const DRAG_THRESHOLD = 4;
  const FOCUSABLE = 'a[href], button, input, select, textarea, [tabindex]';

  class SndHscroll extends HTMLElement {
    connectedCallback() {
      if (!this.wired) {
        this.wired = true;
        this.update = this.update.bind(this);
        this.onPointerDown = this.onPointerDown.bind(this);
        this.onPointerMove = this.onPointerMove.bind(this);
        this.onPointerUp = this.onPointerUp.bind(this);
        this.swallowClick = this.swallowClick.bind(this);
        this.addEventListener('scroll', this.update, { passive: true });
        this.addEventListener('pointerdown', this.onPointerDown);
      }

      if ('ResizeObserver' in window) {
        this.resizeObserver ||= new ResizeObserver(this.update);
        this.resizeObserver.observe(this);
        if (this.firstElementChild) this.resizeObserver.observe(this.firstElementChild);
      }
      window.addEventListener('resize', this.update, { passive: true });

      this.update();
      // Web fonts land after first paint and change how wide the row is.
      document.fonts?.ready.then(this.update).catch(() => {});
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      window.removeEventListener('resize', this.update);
    }

    get overflow() {
      return this.scrollWidth - this.clientWidth;
    }

    update() {
      const max = this.overflow;
      const overflows = max > EDGE;
      const x = this.scrollLeft;

      this.toggleAttribute('data-overflow', overflows);
      this.style.setProperty('--snd-hs-l', overflows && x > EDGE ? 'var(--snd-hs-fade)' : '0px');
      this.style.setProperty('--snd-hs-r', overflows && x < max - EDGE ? 'var(--snd-hs-fade)' : '0px');

      // A scrollable region needs a keyboard route to it (WCAG 2.1.1). Rows that
      // hold a link already have one, so only a link-less row gets a tab stop.
      if (overflows && !this.querySelector(FOCUSABLE)) {
        this.tabIndex = 0;
      } else if (this.hasAttribute('tabindex')) {
        this.removeAttribute('tabindex');
      }
    }

    onPointerDown(event) {
      if (event.pointerType !== 'mouse' || event.button !== 0 || this.overflow <= EDGE) return;
      this.pointerId = event.pointerId;
      this.startX = event.clientX;
      this.startScroll = this.scrollLeft;
      this.dragging = false;
      this.addEventListener('pointermove', this.onPointerMove);
      this.addEventListener('pointerup', this.onPointerUp);
      this.addEventListener('pointercancel', this.onPointerUp);
    }

    onPointerMove(event) {
      if (event.pointerId !== this.pointerId) return;
      const dx = event.clientX - this.startX;

      if (!this.dragging) {
        if (Math.abs(dx) < DRAG_THRESHOLD) return;
        this.dragging = true;
        this.classList.add('is-dragging');
        try {
          this.setPointerCapture(this.pointerId);
        } catch (_) {
          // capture is a nicety; the move listener keeps working without it
        }
      }

      this.scrollLeft = this.startScroll - dx;
      event.preventDefault();
    }

    onPointerUp(event) {
      if (event.pointerId !== this.pointerId) return;
      this.removeEventListener('pointermove', this.onPointerMove);
      this.removeEventListener('pointerup', this.onPointerUp);
      this.removeEventListener('pointercancel', this.onPointerUp);

      try {
        this.releasePointerCapture(this.pointerId);
      } catch (_) {
        // already released
      }
      this.pointerId = null;

      if (this.dragging) {
        this.dragging = false;
        this.classList.remove('is-dragging');
        // The click that ends a drag must not open the link it landed on. It is
        // removed on the next tick either way, so a later real click survives.
        this.addEventListener('click', this.swallowClick, true);
        setTimeout(() => this.removeEventListener('click', this.swallowClick, true), 0);
      }
    }

    swallowClick(event) {
      event.preventDefault();
      event.stopPropagation();
      this.removeEventListener('click', this.swallowClick, true);
    }
  }

  customElements.define('snd-hscroll', SndHscroll);
})();
